import React from 'react';
import { Card, Tag, Typography, Button, Space } from 'antd';
import { EyeOutlined, ShoppingCartOutlined, EnvironmentOutlined, StarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { PLACEHOLDER_IMG, safeImageOnError } from '../../utils/ui';
import { resolveImageSrc } from '../../utils/images';

const { Text, Paragraph } = Typography;

function VendorCard({ vendor, onView, onViewMenu, extraTopRight }) {
	// Safety check for undefined vendor
	if (!vendor) {
		return (
			<Card className="card-ui" size="small" loading>
				<Card.Meta title="Loading..." description="Please wait..." />
			</Card>
		);
	}

    // Derive emoji context for vendors based on cuisines/services
    const emojiContext = (() => {
        try {
            const cuisines = (vendor?.cuisines || []).join(' ').toLowerCase();
            if (/seafood|fish|prawn|shrimp/.test(cuisines)) return 'seafood';
            if (/bakery|bread|naan|roti|paratha/.test(cuisines)) return 'bakery';
            if (/dessert|sweet|mithai|cake|pastry/.test(cuisines)) return 'dessert';
            if (/beverage|juice|tea|coffee|shake|soda/.test(cuisines)) return 'beverage';
            if (/veg|vegetarian/.test(cuisines)) return 'vegetarian';
            return undefined;
        } catch (_) { return undefined; }
    })();

    return (
        <Card 
			className="card-ui modern-food-card hover:shadow-xl transition-all duration-300" 
			size="small"
			bodyStyle={{ padding: '16px', borderRadius: '12px' }}
            data-emoji-context={emojiContext}
            style={{ borderRadius: '12px', border: '1px solid rgba(229, 231, 235, 0.8)' }}
			cover={
				<div className="relative overflow-hidden" style={{ borderRadius: '12px 12px 0 0' }}>
					<img alt={vendor.business_name || 'Vendor'} src={resolveImageSrc(vendor.logo || PLACEHOLDER_IMG)} className="w-full h-40 object-cover transition-transform duration-300 hover:scale-110" onError={safeImageOnError} />
					{extraTopRight && <div className="absolute top-2 right-2">{extraTopRight}</div>}
					<div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/30 to-transparent"></div>
				</div>
			}
			actions={[
				<Button key="view" size="small" icon={<EyeOutlined />} onClick={() => onView?.(vendor)} style={{ borderRadius: '8px', fontWeight: '600' }}>View</Button>,
				<Button key="menu" type="primary" size="small" icon={<ShoppingCartOutlined />} onClick={() => onViewMenu?.(vendor)} className="btn-vibrant" style={{ borderRadius: '8px', background: '#C97D60', border: 'none', fontWeight: '600' }}>Menu</Button>
			]}
		>
			<Card.Meta
				title={
					<div className="flex items-center justify-between mb-2">
						<Text strong className="text-lg font-bold text-gray-900">{vendor.business_name}</Text>
						<div className="rating-modern">
							<StarOutlined className="text-yellow-400 text-base" />
							<Text strong className="text-sm font-bold ml-1">{vendor.rating || 'N/A'}</Text>
						</div>
					</div>
				}
				description={
					<div className="space-y-2">
						<Paragraph ellipsis={{ rows: 2 }} className="mb-0">{vendor.description}</Paragraph>
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-1 text-gray-500">
								<EnvironmentOutlined />
								<Text type="secondary">{vendor.city?.name || 'Location not specified'}</Text>
							</div>
							<div className="flex items-center gap-1 text-gray-500">
								<ClockCircleOutlined />
								<Text type="secondary">{vendor.delivery_time || 'N/A'} min</Text>
							</div>
						</div>
						<div className="flex flex-wrap gap-1">
							{vendor.cuisines?.slice(0, 3).map((cuisine, index) => (
								<Tag key={index} size="small">{cuisine}</Tag>
							))}
						</div>
					</div>
				}
			/>
		</Card>
	);
}

export default VendorCard;
